import { useState } from "react";
import { Tabs } from "@cloudscape-design/components";
import type { TabsWidget, Widget } from "./viewContract";
import { renderWidget } from "./widgetRegistry";

/**
 * Renders a CONTAINER `tabs` widget as CloudScape Tabs (render-pipeline stage 4).
 *
 * Each tab's label is `tab.title`; each tab's CONTENT is its child widget rendered
 * through the SAME registry (`renderWidget`), so the renderer is fully recursive — a
 * tab may itself hold a Table, another Tabs, or an unknown type (which surfaces its own
 * scoped Alert via `renderWidget`).
 *
 * LAZY FETCH: CloudScape's `contentRenderStrategy: "lazy"` renders a tab's content only
 * AFTER the tab is first activated, and then KEEPS it mounted. Because each child table
 * runs its own fetch on mount (stage 4), a tab's child only fetches when that tab is
 * first opened, and switching away and back does NOT remount/refetch it. We still
 * control `activeTabId` so the first tab is the initial active one deterministically.
 */
export default function TabsWidgetView({ widget }: { widget: Widget }) {
  const tabsWidget = widget as TabsWidget;
  const tabs = tabsWidget.tabs ?? [];

  // Controlled active tab; default to the first tab so lazy content for tab 0 renders
  // immediately while later tabs wait for their first activation.
  const [activeTabId, setActiveTabId] = useState<string>("tab-0");

  if (tabs.length === 0) return null;

  return (
    <Tabs
      activeTabId={activeTabId}
      onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
      tabs={tabs.map((tab, index) => ({
        id: `tab-${index}`,
        label: tab.title || `Tab ${index + 1}`,
        // Lazy: the child widget (and its fetch) mounts only when this tab is first opened.
        contentRenderStrategy: "lazy",
        // Recurse through the registry. The child keeps a stable key within this tab.
        content: renderWidget(tab.widget as Widget, index),
      }))}
    />
  );
}
